import { useEffect, useState, useCallback } from "react";
import api, { errMsg } from "../lib/api";
import { fmtIDR, fmtDate, today } from "../lib/format";
import { PageHeader, Card, Badge, Modal, Field, Btn, DataTable, inputCls } from "../components/common";
import { useAuth } from "../context/AuthContext";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  ["jurnal", "Jurnal Umum"], ["coa", "Bagan Akun (COA)"], ["buku-besar", "Buku Besar"],
  ["laba-rugi", "Laba / Rugi"], ["neraca", "Neraca"], ["arus-kas", "Arus Kas"], ["neraca-saldo", "Neraca Saldo"],
];

export default function Akuntansi() {
  const { hasPerm, projects, projectId } = useAuth();
  const [tab, setTab] = useState("jurnal");
  const [accProject, setAccProject] = useState("");
  const [coa, setCoa] = useState([]);
  const [journals, setJournals] = useState([]);
  const [report, setReport] = useState(null);
  const [showJournal, setShowJournal] = useState(false);
  const [showCoa, setShowCoa] = useState(false);
  const [coaForm, setCoaForm] = useState({ code: "", name: "", type: "asset", normal: "debit" });
  const [jf, setJf] = useState({ date: today(), description: "", project_id: "", lines: [{ account_id: "", debit: 0, credit: 0 }, { account_id: "", debit: 0, credit: 0 }] });
  const [ledgerAcc, setLedgerAcc] = useState("");

  const load = useCallback(() => {
    api.get("/accounting/coa").then((r) => setCoa(r.data));
    api.get(`/accounting/journals${accProject ? `?project_id=${accProject}` : ""}`).then((r) => setJournals(r.data));
  }, [accProject]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (["buku-besar", "laba-rugi", "neraca", "arus-kas", "neraca-saldo"].includes(tab)) {
      const params = new URLSearchParams();
      if (tab === "buku-besar" && ledgerAcc) params.set("account_id", ledgerAcc);
      if (accProject) params.set("project_id", accProject);
      const q = params.toString() ? `?${params.toString()}` : "";
      api.get(`/accounting/reports/${tab}${q}`).then((r) => setReport(r.data)).catch((e) => toast.error(errMsg(e)));
    }
  }, [tab, ledgerAcc, accProject]);

  const totalD = jf.lines.reduce((s, l) => s + (+l.debit || 0), 0);
  const totalC = jf.lines.reduce((s, l) => s + (+l.credit || 0), 0);
  const balanced = Math.round(totalD) === Math.round(totalC) && totalD > 0;

  const saveJournal = async () => {
    try {
      await api.post("/accounting/journals", { ...jf, lines: jf.lines.filter((l) => l.account_id && (l.debit || l.credit)) });
      toast.success("Jurnal berhasil diposting");
      setShowJournal(false);
      setJf({ date: today(), description: "", lines: [{ account_id: "", debit: 0, credit: 0 }, { account_id: "", debit: 0, credit: 0 }] });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveCoa = async () => {
    try {
      await api.post("/accounting/coa", coaForm);
      toast.success("Akun ditambahkan");
      setShowCoa(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const delJournal = async (id) => {
    if (!window.confirm("Hapus jurnal ini?")) return;
    try {
      const res = await api.delete(`/accounting/journals/${id}`);
      if (res.data?.status === "pending_approval") toast.info(res.data.message);
      else toast.success("Jurnal dihapus");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const ReportTable = ({ rows, columns }) => (
    <div className="overflow-x-auto rounded-xl border border-slate-800">
      <table className="w-full text-sm">
        <thead><tr className="bg-slate-900 border-b border-slate-800">{columns.map((c) => <th key={c} className="text-left text-xs font-mono uppercase tracking-wider text-slate-500 px-4 py-3">{c}</th>)}</tr></thead>
        <tbody>{rows}</tbody>
      </table>
    </div>
  );

  return (
    <div data-testid="akuntansi-page">
      <PageHeader title="Akuntansi" subtitle="Double-entry bookkeeping — jurnal otomatis dari Penjualan, Purchase & Proyek" testid="akuntansi-header">
        {hasPerm("akuntansi", "create") && (
          <>
            <Btn tone="ghost" onClick={() => setShowCoa(true)} data-testid="add-coa-button"><Plus className="w-4 h-4" /> Akun</Btn>
            <Btn onClick={() => setShowJournal(true)} data-testid="add-journal-button"><Plus className="w-4 h-4" /> Jurnal Manual</Btn>
          </>
        )}
      </PageHeader>

      <div className="mb-4 max-w-xs">
        <select className={inputCls} value={accProject} onChange={(e) => setAccProject(e.target.value)} data-testid="acc-project-filter">
          <option value="">Konsolidasi (Semua Proyek)</option>
          {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      <div className="flex gap-2 mb-5 flex-wrap">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`acc-tab-${k}`}
            className={`text-xs sm:text-sm px-3.5 py-2 rounded-lg border transition-colors ${tab === k ? "bg-emerald-600/10 text-emerald-300 border-emerald-500/50" : "text-slate-400 border-slate-800 hover:bg-slate-900 hover:text-slate-200"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "jurnal" && (
        <div className="space-y-3" data-testid="journal-list">
          {journals.length === 0 && <Card><p className="text-slate-500 text-sm">Belum ada jurnal.</p></Card>}
          {journals.map((j) => (
            <Card key={j.id} testid={`journal-${j.number}`} className="!p-4">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-sm text-emerald-400">{j.number}</span>
                  <span className="text-xs text-slate-500">{fmtDate(j.date)}</span>
                  <Badge tone={j.source === "manual" ? "slate" : "cyan"}>{j.source}</Badge>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-xs text-slate-300">{fmtIDR(j.total)}</span>
                  {hasPerm("akuntansi", "delete") && <button onClick={() => delJournal(j.id)} className="text-slate-600 hover:text-rose-400" data-testid={`journal-delete-${j.number}`}><Trash2 className="w-4 h-4" /></button>}
                </div>
              </div>
              <div className="text-xs text-slate-400 mb-2">{j.description}</div>
              <table className="w-full text-xs">
                <tbody>
                  {j.lines.map((l, i) => (
                    <tr key={i} className="border-t border-slate-800/60">
                      <td className="py-1.5 pr-4 font-mono text-slate-400">{l.account_code}</td>
                      <td className="py-1.5 text-slate-300">{l.account_name}</td>
                      <td className="py-1.5 text-right font-mono text-slate-300 w-32">{l.debit ? fmtIDR(l.debit) : ""}</td>
                      <td className="py-1.5 text-right font-mono text-slate-300 w-32">{l.credit ? fmtIDR(l.credit) : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          ))}
        </div>
      )}

      {tab === "coa" && (
        <DataTable testid="coa-table" searchKeys={["code", "name"]}
          columns={[
            { header: "Kode", accessor: "code", mono: true },
            { header: "Nama Akun", accessor: "name" },
            { header: "Tipe", render: (r) => <Badge tone={{ asset: "emerald", liability: "amber", equity: "indigo", revenue: "cyan", expense: "rose" }[r.type]}>{r.type}</Badge> },
            { header: "Saldo Normal", accessor: "normal", mono: true },
          ]}
          rows={coa} />
      )}

      {tab === "buku-besar" && (
        <div>
          <div className="mb-3 max-w-xs">
            <select className={inputCls} value={ledgerAcc} onChange={(e) => setLedgerAcc(e.target.value)} data-testid="ledger-account-select">
              <option value="">Semua Akun</option>
              {coa.map((a) => <option key={a.id} value={a.id}>{a.code} - {a.name}</option>)}
            </select>
          </div>
          <ReportTable columns={["No. Jurnal", "Tanggal", "Akun", "Keterangan", "Debit", "Kredit"]}
            rows={(report || []).map((r, i) => (
              <tr key={i} className="border-b border-slate-800/60">
                <td className="px-4 py-2.5 font-mono text-xs text-emerald-400">{r.journal_number}</td>
                <td className="px-4 py-2.5 text-xs text-slate-400">{fmtDate(r.date)}</td>
                <td className="px-4 py-2.5 text-xs text-slate-300"><span className="font-mono text-slate-500">{r.account_code}</span> {r.account_name}</td>
                <td className="px-4 py-2.5 text-xs text-slate-400">{r.description}</td>
                <td className="px-4 py-2.5 text-right font-mono text-xs text-slate-300">{r.debit ? fmtIDR(r.debit) : ""}</td>
                <td className="px-4 py-2.5 text-right font-mono text-xs text-slate-300">{r.credit ? fmtIDR(r.credit) : ""}</td>
              </tr>
            ))} />
        </div>
      )}

      {tab === "laba-rugi" && report && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="laba-rugi-report">
          <Card testid="laba-rugi-revenue">
            <h3 className="text-sm font-medium text-emerald-400 mb-3">Pendapatan</h3>
            {report.revenue.map((r) => (
              <div key={r.code} className="flex justify-between text-sm py-1.5 border-b border-slate-800/60">
                <span className="text-slate-300"><span className="font-mono text-xs text-slate-500 mr-2">{r.code}</span>{r.name}</span>
                <span className="font-mono text-xs text-slate-200">{fmtIDR(r.amount)}</span>
              </div>
            ))}
            <div className="flex justify-between pt-3 font-medium text-sm"><span className="text-emerald-400">Total Pendapatan</span><span className="font-mono text-emerald-400">{fmtIDR(report.total_revenue)}</span></div>
          </Card>
          <Card testid="laba-rugi-expense">
            <h3 className="text-sm font-medium text-rose-400 mb-3">Beban</h3>
            {report.expense.map((r) => (
              <div key={r.code} className="flex justify-between text-sm py-1.5 border-b border-slate-800/60">
                <span className="text-slate-300"><span className="font-mono text-xs text-slate-500 mr-2">{r.code}</span>{r.name}</span>
                <span className="font-mono text-xs text-slate-200">{fmtIDR(r.amount)}</span>
              </div>
            ))}
            <div className="flex justify-between pt-3 font-medium text-sm"><span className="text-rose-400">Total Beban</span><span className="font-mono text-rose-400">{fmtIDR(report.total_expense)}</span></div>
          </Card>
          <Card className="md:col-span-2" testid="laba-rugi-net">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-slate-200">Laba Bersih</span>
              <span className={`font-mono text-xl font-semibold ${report.net_income >= 0 ? "text-emerald-400" : "text-rose-400"}`} data-testid="net-income-value">{fmtIDR(report.net_income)}</span>
            </div>
          </Card>
        </div>
      )}

      {tab === "neraca" && report && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="neraca-report">
          <Card testid="neraca-assets">
            <h3 className="text-sm font-medium text-emerald-400 mb-3">Aset</h3>
            {report.assets.map((r) => (
              <div key={r.code} className="flex justify-between text-sm py-1.5 border-b border-slate-800/60">
                <span className="text-slate-300"><span className="font-mono text-xs text-slate-500 mr-2">{r.code}</span>{r.name}</span>
                <span className="font-mono text-xs text-slate-200">{fmtIDR(r.amount)}</span>
              </div>
            ))}
            <div className="flex justify-between pt-3 font-medium text-sm"><span className="text-emerald-400">Total Aset</span><span className="font-mono text-emerald-400" data-testid="total-assets">{fmtIDR(report.total_assets)}</span></div>
          </Card>
          <div className="space-y-4">
            <Card testid="neraca-liabilities">
              <h3 className="text-sm font-medium text-amber-400 mb-3">Kewajiban</h3>
              {report.liabilities.map((r) => (
                <div key={r.code} className="flex justify-between text-sm py-1.5 border-b border-slate-800/60">
                  <span className="text-slate-300"><span className="font-mono text-xs text-slate-500 mr-2">{r.code}</span>{r.name}</span>
                  <span className="font-mono text-xs text-slate-200">{fmtIDR(r.amount)}</span>
                </div>
              ))}
              <div className="flex justify-between pt-3 text-sm"><span className="text-amber-400">Total Kewajiban</span><span className="font-mono text-amber-400">{fmtIDR(report.total_liabilities)}</span></div>
            </Card>
            <Card testid="neraca-equity">
              <h3 className="text-sm font-medium text-indigo-400 mb-3">Ekuitas</h3>
              {report.equity.map((r, i) => (
                <div key={i} className="flex justify-between text-sm py-1.5 border-b border-slate-800/60">
                  <span className="text-slate-300">{r.name}</span>
                  <span className="font-mono text-xs text-slate-200">{fmtIDR(r.amount)}</span>
                </div>
              ))}
              <div className="flex justify-between pt-3 text-sm"><span className="text-indigo-400">Total Ekuitas</span><span className="font-mono text-indigo-400" data-testid="total-equity">{fmtIDR(report.total_equity)}</span></div>
            </Card>
          </div>
        </div>
      )}

      {tab === "arus-kas" && report && (
        <ReportTable columns={["No. Jurnal", "Tanggal", "Kategori", "Keterangan", "Masuk", "Keluar"]}
          rows={report.map((r, i) => (
            <tr key={i} className="border-b border-slate-800/60">
              <td className="px-4 py-2.5 font-mono text-xs text-emerald-400">{r.number}</td>
              <td className="px-4 py-2.5 text-xs text-slate-400">{fmtDate(r.date)}</td>
              <td className="px-4 py-2.5"><Badge tone={r.category === "operasi" ? "cyan" : "indigo"}>{r.category}</Badge></td>
              <td className="px-4 py-2.5 text-xs text-slate-400">{r.description}</td>
              <td className="px-4 py-2.5 text-right font-mono text-xs text-emerald-400">{r.in ? fmtIDR(r.in) : ""}</td>
              <td className="px-4 py-2.5 text-right font-mono text-xs text-rose-400">{r.out ? fmtIDR(r.out) : ""}</td>
            </tr>
          ))} />
      )}

      {tab === "neraca-saldo" && report && (
        <ReportTable columns={["Kode", "Akun", "Tipe", "Debit", "Kredit"]}
          rows={report.map((r) => (
            <tr key={r.code} className="border-b border-slate-800/60">
              <td className="px-4 py-2.5 font-mono text-xs text-slate-400">{r.code}</td>
              <td className="px-4 py-2.5 text-sm text-slate-300">{r.name}</td>
              <td className="px-4 py-2.5 text-xs text-slate-500">{r.type}</td>
              <td className="px-4 py-2.5 text-right font-mono text-xs text-slate-300">{r.debit ? fmtIDR(r.debit) : ""}</td>
              <td className="px-4 py-2.5 text-right font-mono text-xs text-slate-300">{r.credit ? fmtIDR(r.credit) : ""}</td>
            </tr>
          ))} />
      )}

      <Modal open={showJournal} onClose={() => setShowJournal(false)} title="Jurnal Umum Manual" wide testid="journal-form-modal">
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Tanggal"><input type="date" className={inputCls} value={jf.date} onChange={(e) => setJf({ ...jf, date: e.target.value })} data-testid="journal-date-input" /></Field>
            <Field label="Keterangan"><input className={inputCls} value={jf.description} onChange={(e) => setJf({ ...jf, description: e.target.value })} data-testid="journal-desc-input" /></Field>
          </div>
          <Field label="Cost Center (Proyek)">
            <select className={inputCls} value={jf.project_id || ""} onChange={(e) => setJf({ ...jf, project_id: e.target.value || null })} data-testid="journal-project-select">
              <option value="">Global / Kantor Pusat</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </Field>
          <div className="space-y-2">
            {jf.lines.map((l, i) => (
              <div key={i} className="grid grid-cols-[1fr_130px_130px_32px] gap-2 items-center">
                <select className={inputCls} value={l.account_id} onChange={(e) => setJf({ ...jf, lines: jf.lines.map((x, xi) => xi === i ? { ...x, account_id: e.target.value } : x) })} data-testid={`journal-account-${i}`}>
                  <option value="">Pilih akun...</option>
                  {coa.map((a) => <option key={a.id} value={a.id}>{a.code} - {a.name}</option>)}
                </select>
                <input type="number" placeholder="Debit" className={inputCls} value={l.debit || ""} onChange={(e) => setJf({ ...jf, lines: jf.lines.map((x, xi) => xi === i ? { ...x, debit: +e.target.value, credit: 0 } : x) })} data-testid={i === 0 ? "journal-debit-input" : `journal-debit-${i}`} />
                <input type="number" placeholder="Kredit" className={inputCls} value={l.credit || ""} onChange={(e) => setJf({ ...jf, lines: jf.lines.map((x, xi) => xi === i ? { ...x, credit: +e.target.value, debit: 0 } : x) })} data-testid={`journal-credit-${i}`} />
                <button className="text-slate-600 hover:text-rose-400" onClick={() => setJf({ ...jf, lines: jf.lines.filter((_, xi) => xi !== i) })}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <Btn tone="ghost" className="!text-xs" onClick={() => setJf({ ...jf, lines: [...jf.lines, { account_id: "", debit: 0, credit: 0 }] })} data-testid="journal-add-line"><Plus className="w-3.5 h-3.5" /> Baris</Btn>
          </div>
          <div className={`flex justify-between items-center rounded-lg border px-4 py-2.5 text-sm font-mono ${balanced ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/5" : "border-rose-500/40 text-rose-300 bg-rose-500/5"}`} data-testid="journal-balance-indicator">
            <span>D: {fmtIDR(totalD)}</span>
            <span>{balanced ? "BALANCE" : "TIDAK BALANCE"}</span>
            <span>K: {fmtIDR(totalC)}</span>
          </div>
          <div className="flex justify-end gap-2">
            <Btn tone="ghost" onClick={() => setShowJournal(false)}>Batal</Btn>
            <Btn onClick={saveJournal} disabled={!balanced} data-testid="journal-save-button">Posting Jurnal</Btn>
          </div>
        </div>
      </Modal>

      <Modal open={showCoa} onClose={() => setShowCoa(false)} title="Tambah Akun (COA)" testid="coa-form-modal">
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Kode"><input className={inputCls} value={coaForm.code} onChange={(e) => setCoaForm({ ...coaForm, code: e.target.value })} data-testid="coa-code-input" /></Field>
            <Field label="Nama Akun"><input className={inputCls} value={coaForm.name} onChange={(e) => setCoaForm({ ...coaForm, name: e.target.value })} data-testid="coa-name-input" /></Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Tipe">
              <select className={inputCls} value={coaForm.type} onChange={(e) => setCoaForm({ ...coaForm, type: e.target.value })} data-testid="coa-type-select">
                <option value="asset">Aset</option><option value="liability">Kewajiban</option><option value="equity">Ekuitas</option>
                <option value="revenue">Pendapatan</option><option value="expense">Beban</option>
              </select>
            </Field>
            <Field label="Saldo Normal">
              <select className={inputCls} value={coaForm.normal} onChange={(e) => setCoaForm({ ...coaForm, normal: e.target.value })} data-testid="coa-normal-select">
                <option value="debit">Debit</option><option value="credit">Kredit</option>
              </select>
            </Field>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Btn tone="ghost" onClick={() => setShowCoa(false)}>Batal</Btn>
            <Btn onClick={saveCoa} data-testid="coa-save-button">Simpan</Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
}
